<!-- ActivityGeneralForm.vue -->
<!-- Componente de pesquisa de atividades gerais de usuários.           -->
<!-- Filtros: id atividade, id texto, título atividade, título texto,   -->
<!--          id criador, nome criador, data início, progresso.         -->
<!-- Padrão visual/lógico baseado em SubscriptionForm.vue.              -->
<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import debounce from "lodash.debounce";

import { toTitleCase } from "@/utils/stringUtils";
import {
  PROGRESS_OPTIONS,
  progressShortLabel,
  progressFullLabel,
  isSameProgress,
} from "@/utils/activityProgress";
import { formatDateTimeForInput } from "@/utils/dateUtils";

// Stores
import { useActivityGeneralStore } from "@/stores/useActivityGeneralStore";
import { useUserStore } from "@/stores/useUserStore";
import { useActivityStore } from "@/stores/useActivityStore";
// ── NOVO: guard de permissão por vínculo Membro-Titular ───────────────────────
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Modais
import ActivityCardModal from "@/components/Activity/ActivityCardModal.vue";
import EditActivityGeneralForm from "./EditActivityGeneralForm.vue";
import DeleteActivityGeneralForm from "./DeleteActivityGeneralForm.vue";
import BatchDeleteActivityGeneralForm from "./BatchDeleteActivityGeneralForm.vue";
import PermissionView from "@/components/Generics/PermissionView.vue";

// ─────────────────────────────────────────────────────────────────────────────
// STORES & ROLE
// ─────────────────────────────────────────────────────────────────────────────
const store = useActivityGeneralStore();
const userStore = useUserStore();
const activityStore = useActivityStore();
const router = useRouter();
// ── NOVO ──────────────────────────────────────────────────────────────────────
const managedAccountStore = useManagedAccountStore();

const isSys = computed(() => userStore.currentUser?.userName === "Sys");

// ── NOVO: permissões efetivas do vínculo para atividades ─────────────────────
const memberActivityPermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.activities ?? null
);
const memberCanEditActivity = computed(
  () =>
    memberActivityPermissions.value === null ||
    memberActivityPermissions.value.edit
);
const memberCanDeleteActivity = computed(
  () =>
    memberActivityPermissions.value === null ||
    memberActivityPermissions.value.delete
);

// ── NOVO: guard de notas — portão para o botão "Anotar" na linha ──────────────
const memberNotePermissions = computed(
  () => managedAccountStore.currentMemberPermissions?.notes ?? null
);
const memberCanViewNotes = computed(
  () => memberNotePermissions.value === null || memberNotePermissions.value.view
);

// ─────────────────────────────────────────────────────────────────────────────
// NAVEGAÇÃO — ANOTAR
// ─────────────────────────────────────────────────────────────────────────────
async function goToRightArea(act) {
  try {
    await activityStore.openTextForAnnotation(act.textId, {
      preferActivityId: act.activityId,
    });

    router.push({ name: "RightArea" });
  } catch (error) {
    console.error("❌ Erro ao abrir a atividade para anotação:", error);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REFS DE DOM — cálculo dinâmico da altura da tabela
// ─────────────────────────────────────────────────────────────────────────────
// A rolagem é do card inteiro (.main-container): filtros, tabela e paginador
// sobem juntos. Não há mais altura calculada para a tabela.

const openDropdownId = ref(null);
const dropdownPosition = ref({ top: 0, left: 0, ready: false });
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ── Dropdown de progresso por linha (espelho do ActivityForm) ─────────────────
const openProgressDropdownId = ref(null);
const progressDropdownPosition = ref({ top: 0, left: 0, ready: false });
const isProgressUpdating = ref(false);

// ─────────────────────────────────────────────────────────────────────────────
// POSICIONAMENTO DOS MENUS TELEPORTADOS
// ─────────────────────────────────────────────────────────────────────────────
// Os dois menus de linha (ações e progresso) vão para o <body> com
// position: fixed — as coordenadas são da viewport e o elemento fica fora do
// fluxo de rolagem. Posicionar "colado no botão" sem olhar o espaço disponível
// corta o menu nas últimas linhas da tabela, e rolar não recupera nada: o menu
// é fixo, acompanha a viewport e continua amputado do mesmo jeito.
const VIEWPORT_MARGIN = 8;

function computeFixedMenuPosition(menuEl, anchorRect, placement) {
  const { innerWidth: vw, innerHeight: vh } = window;
  const w = menuEl.offsetWidth;
  const h = menuEl.offsetHeight;
  const m = VIEWPORT_MARGIN;

  let top;
  let left;

  if (placement === "below") {
    // Progresso: abaixo do botão; sem espaço embaixo, vira para cima.
    const gap = 4;
    top = anchorRect.bottom + gap;
    if (top + h > vh - m && anchorRect.top - h - gap >= m) {
      top = anchorRect.top - h - gap;
    }
    left = anchorRect.left;
  } else {
    // Ações: ao lado do botão, alinhado pelo topo; sem espaço à direita,
    // vira para a esquerda.
    const gap = 8;
    top = anchorRect.top;
    left = anchorRect.right + gap;
    if (left + w > vw - m && anchorRect.left - w - gap >= m) {
      left = anchorRect.left - w - gap;
    }
  }

  // Rede de segurança para janelas curtas/estreitas demais para qualquer lado.
  // O Math.max externo evita um teto menor que a margem quando o menu não cabe
  // de jeito nenhum — aí ele encosta na borda e rola por dentro (max-height).
  top = Math.min(Math.max(top, m), Math.max(m, vh - h - m));
  left = Math.min(Math.max(left, m), Math.max(m, vw - w - m));

  return { top, left };
}

// A altura real só existe depois do v-if renderizar o menu, por isso a medição
// vem no nextTick. Até lá o menu fica com visibility: hidden, para não piscar
// um quadro na posição provisória.
async function positionFixedMenu(selector, anchorRect, placement, positionRef) {
  await nextTick();
  const menuEl = document.querySelector(selector);
  if (!menuEl) return;
  positionRef.value = {
    ...computeFixedMenuPosition(menuEl, anchorRect, placement),
    ready: true,
  };
}

function closeRowMenus() {
  openDropdownId.value = null;
  openProgressDropdownId.value = null;
}

// Menu fixo não acompanha a rolagem da tabela: ao rolar, ele descola da linha
// que o abriu. Fechar é mais honesto do que reposicionar a cada quadro.
// Capture porque o evento scroll não borbulha até window.
function handleViewportChange(event) {
  const target = event?.target;
  if (
    target instanceof Element &&
    target.closest(".progress-dropdown-menu, .row-dropdown-menu")
  ) {
    return; // rolagem interna do próprio menu
  }
  closeRowMenus();
}

// Vocabulário do progresso: PROGRESS_OPTIONS e rótulos vêm de
// @/utils/activityProgress, compartilhado com as demais telas de atividade.

// ─────────────────────────────────────────────────────────────────────────────
// MODAIS
// ─────────────────────────────────────────────────────────────────────────────
const showCardModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);
const showPermissionViewModal = ref(false);
const selectedActivity = ref(null);
const selectedActivityId = ref(null);
const selectedBatchIds = ref([]);

// ✅ FIX: marca se a atividade foi editada/excluída enquanto o card estava aberto.
// Só recarregamos a tabela ao fechar o card SE houve alteração de fato.
const cardActivityChanged = ref(false);

// ─────────────────────────────────────────────────────────────────────────────
// PAGINAÇÃO
// ─────────────────────────────────────────────────────────────────────────────
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ PATCH: controla o estado de carregamento das atividades
const isLoadingActivities = ref(false);

// ✅ PATCH: evita que uma requisição antiga desligue o loading de uma requisição nova
const fetchActivitiesRequestId = ref(0);

// ─────────────────────────────────────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────────────────────────────────────
const showToast = ref(false);
const toastMessage = ref("");
const toastType = ref("error");
const toastTimeoutId = ref(null);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}
function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}
function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function handleChildNotify(payload) {
  if (!payload) return;
  if (payload.type === "error")
    showErrorToast(payload.message || "Ocorreu um erro.");
  else showSuccessToast(payload.message || "Operação realizada com sucesso.");
}

// ✅ FIX: notificações vindas do ActivityCardModal (exclusão pelo card).
// Só exibimos SUCESSO aqui — o erro continua a cargo do DeleteConfirmModal, que mantém
// o card aberto e já mostra o toast de erro (evita duplicidade). Necessário porque, ao
// apagar pelo card, o card fecha imediatamente e o toast interno é desmontado junto.
function handleCardNotify(payload) {
  if (!payload) return;
  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FILTROS
// ─────────────────────────────────────────────────────────────────────────────
const local = reactive({
  activityId: store.filters.activityId,
  textId: store.filters.textId,
  activityTitle: store.filters.activityTitle,
  textTitle: store.filters.textTitle,
  permissionGroupName: store.filters.permissionGroupName,
  permissionUserName: store.filters.permissionUserName,
  creatorId: store.filters.creatorId,
  creatorName: store.filters.creatorName,
  startDateFrom: store.filters.startDateFrom,
  startDateTo: store.filters.startDateTo,
  progress: store.filters.progress,
  // espelha onlyMine da store — vira chip como qualquer outra condição
  onlyMine: store.filters.onlyMine ?? false,
});

const debouncedNumeric = debounce((field, value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0))
    return;
  store.setFilter(field, trimmed);
}, 400);

const debouncedText = debounce((field, value) => {
  store.setFilter(field, String(value ?? "").trim());
}, 300);

watch(
  () => local.activityId,
  (v) => debouncedNumeric("activityId", v)
);
watch(
  () => local.textId,
  (v) => debouncedNumeric("textId", v)
);
watch(
  () => local.activityTitle,
  (v) => debouncedText("activityTitle", v)
);
watch(
  () => local.textTitle,
  (v) => debouncedText("textTitle", v)
);
watch(
  () => local.permissionGroupName,
  (v) => debouncedText("permissionGroupName", v)
);
watch(
  () => local.permissionUserName,
  (v) => debouncedText("permissionUserName", v)
);
watch(
  () => local.creatorId,
  (v) => debouncedNumeric("creatorId", v)
);
watch(
  () => local.creatorName,
  (v) => debouncedText("creatorName", v)
);
watch(
  () => local.startDateFrom,
  (v) => debouncedText("startDateFrom", v)
);
watch(
  () => local.startDateTo,
  (v) => debouncedText("startDateTo", v)
);
watch(
  () => local.progress,
  (v) => store.setFilter("progress", v)
);
watch(
  () => local.onlyMine,
  (v) => store.setFilter("onlyMine", v)
);

// Único disparador de busca por mudança de filtro: qualquer setFilter/clearFilters
// cai aqui, volta à página 1 e refaz a consulta. Fora da página 1 quem busca é o
// watcher de currentPage — chamar fetchActivities() aqui também duplicaria a
// requisição. Mesmo desenho de NoteGeneralForm.vue.
watch(
  () => ({ ...store.filters }),
  () => {
    if (currentPage.value !== 1) currentPage.value = 1;
    else fetchActivities();
  },
  { deep: true }
);

function clearAllFilters() {
  // Debounces em voo só reaplicariam valores antigos (e uma busca redundante)
  // depois que clearFilters() já zerou tudo na store.
  debouncedText.cancel();
  debouncedNumeric.cancel();

  Object.keys(local).forEach((k) => {
    local[k] = typeof local[k] === "boolean" ? false : "";
  });

  activeFilterKeys.value = [];
  addedInThisSession.value = [];
  isAddFilterOpen.value = false;

  // Zera os filtros da store; o watcher acima volta à página 1 e refaz a busca.
  store.clearFilters();
}

const hasActiveFilters = computed(() =>
  Object.entries(local).some(([, v]) =>
    typeof v === "boolean" ? v === true : String(v ?? "").trim() !== ""
  )
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIS FILTROS (painel "Filtros ativos" + chips) — espelha NoteGeneralForm.vue
// ─────────────────────────────────────────────────────────────────────────────
// `short` + `verb` compõem o rótulo da chip (ex.: Criador = Maria).
const ADVANCED_FILTERS = [
  {
    key: "creatorName",
    label: "Criador",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Criador",
    verb: "=",
    icon: "fas fa-user",
  },
  {
    key: "creatorId",
    label: "ID do criador",
    type: "numeric",
    placeholder: "ex: 7",
    short: "ID do criador",
    verb: "=",
    icon: "fas fa-id-badge",
  },
  {
    key: "startRange",
    label: "Data de início",
    type: "dateRange",
    fromKey: "startDateFrom",
    toKey: "startDateTo",
    short: "Início",
    icon: "fas fa-calendar-alt",
  },
  // Fecha a primeira coluna do menu "Adicionar filtro"
  {
    key: "progress",
    label: "Progresso",
    type: "select",
    // `value` é o que vai ao backend (Activity.Status); `label` é o rótulo curto,
    // o mesmo da coluna e do menu de progresso. A ordem segue o fluxo de trabalho.
    options: PROGRESS_OPTIONS.map((o) => ({
      value: o.value,
      label: o.short,
    })),
    short: "Progresso",
    verb: "=",
    // Sem isto a chip mostraria o valor cru ("Concluída para correção")
    formatValue: progressShortLabel,
    icon: "fas fa-tasks",
  },
  {
    key: "activityId",
    label: "ID da atividade",
    type: "numeric",
    placeholder: "ex: 12",
    short: "ID da atividade",
    verb: "=",
    icon: "fas fa-barcode",
  },
  {
    key: "textId",
    label: "ID do texto",
    type: "numeric",
    placeholder: "ex: 55",
    short: "ID do texto",
    verb: "=",
    icon: "fas fa-fingerprint",
  },
  {
    key: "permissionGroupName",
    label: "Compartilhada com grupo",
    type: "text",
    placeholder: "ex: Crianças…",
    short: "Compartilhada com grupo",
    verb: "quoted",
    icon: "fas fa-users-cog",
  },
  {
    key: "permissionUserName",
    label: "Compartilhada com usuário",
    type: "text",
    placeholder: "ex: Maria…",
    short: "Compartilhada com usuário",
    verb: "quoted",
    icon: "fas fa-user-tag",
  },
];

// Filtros da linha principal — não entram no dropdown, mas viram chip no painel.
const BASE_FILTERS = [
  {
    key: "activityTitle",
    type: "text",
    short: "Título da atividade",
    verb: "contém",
    icon: "fas fa-file-signature",
  },
  {
    key: "textTitle",
    type: "text",
    short: "Título do texto",
    verb: "contém",
    icon: "fas fa-book",
  },
  // Botão "Minhas atividades" — vira chip como qualquer outra condição.
  {
    key: "onlyMine",
    type: "checkbox",
    short: "Minhas atividades",
    icon: "fas fa-user",
  },
];
const ALL_FILTERS = [...BASE_FILTERS, ...ADVANCED_FILTERS];

const isAdvancedPanelOpen = ref(false);
const isAddFilterOpen = ref(false);
const addFilterMenuRef = ref(null);
const activeFilterKeys = ref([]);
const addedInThisSession = ref([]);

// Controles da linha principal — alvo do clique nas chips de filtro base.
const activityTitleInputRef = ref(null);
const textTitleInputRef = ref(null);
const myActivitiesButtonRef = ref(null);

// Duas colunas independentes: abrir um campo cresce só a coluna dele.
const addFilterColumns = computed(() => {
  const options = addFilterOptions.value;
  const half = Math.ceil(options.length / 2);
  return [options.slice(0, half), options.slice(half)];
});

const activeFilters = computed(() =>
  activeFilterKeys.value
    .map((key) => ADVANCED_FILTERS.find((f) => f.key === key))
    .filter(Boolean)
);

// Filtros ainda não adicionados — é o que o dropdown mostra ao ser aberto.
const remainingFilters = computed(() =>
  ADVANCED_FILTERS.filter((f) => !activeFilterKeys.value.includes(f.key))
);

// Um filtro adicionado continua listado enquanto o dropdown permanecer aberto,
// para que o campo de preenchimento fique visível abaixo do seu nome.
const addFilterOptions = computed(() =>
  ADVANCED_FILTERS.filter(
    (f) =>
      !activeFilterKeys.value.includes(f.key) ||
      addedInThisSession.value.includes(f.key)
  )
);

function isFilterAdded(key) {
  return activeFilterKeys.value.includes(key);
}

// Um filtro só vira chip quando tem valor — opção clicada e não preenchida não conta.
function hasFilterValue(filter) {
  if (!filter) return false;
  if (filter.type === "checkbox") return local[filter.key] === true;
  if (filter.type === "dateRange")
    return Boolean(local[filter.fromKey] || local[filter.toKey]);
  return String(local[filter.key] ?? "").trim() !== "";
}

const chipFilters = computed(() => [
  ...BASE_FILTERS.filter(hasFilterValue),
  ...activeFilters.value.filter(hasFilterValue),
]);

// Filtros com controle próprio à vista (campos da linha principal e o botão
// "Minhas atividades"): sozinhos, não abrem o painel.
const PRIMARY_KEYS = ["activityTitle", "textTitle", "onlyMine"];
const panelOnlyChips = computed(() =>
  chipFilters.value.filter((f) => !PRIMARY_KEYS.includes(f.key))
);

const isPanelVisible = computed(
  () => isAdvancedPanelOpen.value || panelOnlyChips.value.length > 0
);

// Descarta as opções abertas no dropdown que ficaram sem preenchimento.
function pruneEmptyFilters() {
  activeFilterKeys.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  addedInThisSession.value = [];
}

function closeAddFilterDropdown() {
  isAddFilterOpen.value = false;
  pruneEmptyFilters();
}

function toggleAdvancedPanel() {
  isAdvancedPanelOpen.value = !isAdvancedPanelOpen.value;
  if (!isAdvancedPanelOpen.value) closeAddFilterDropdown();
}

// O menu pode abrir mesmo sem filtros restantes: opções com chip reabrem em edição.
const hasAddFilterContent = computed(
  () => remainingFilters.value.length > 0 || panelOnlyChips.value.length > 0
);

// Abre o dropdown com uma opção em modo de edição. Sem `focusKey`, cai na
// primeira opção disponível (comportamento do botão "Adicionar filtro").
function openAddFilterDropdown(focusKey = null) {
  if (!hasAddFilterContent.value) return;
  isAddFilterOpen.value = true;
  // Opções que já têm chip entram listadas e expandidas, exibindo seus valores.
  addedInThisSession.value = activeFilterKeys.value.filter((key) =>
    hasFilterValue(ADVANCED_FILTERS.find((f) => f.key === key))
  );
  const target =
    focusKey && addFilterOptions.value.some((f) => f.key === focusKey)
      ? focusKey
      : addFilterOptions.value[0]?.key;
  if (target) selectFilterOption(target);
}

function toggleAddFilterDropdown() {
  if (isAddFilterOpen.value) {
    closeAddFilterDropdown();
    return;
  }
  openAddFilterDropdown();
}

// Seleção (via setas ou abertura do menu) já coloca o campo em modo de edição:
// expande a opção e foca o input, sem precisar de Enter. Checkbox não expande —
// marcar/desmarcar fica no Espaço para não ativar o filtro só de passagem.
function selectFilterOption(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def) return;
  if (def.type !== "checkbox" && !activeFilterKeys.value.includes(key)) {
    activeFilterKeys.value.push(key);
    addedInThisSession.value.push(key);
  }
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    if (!option) return;
    const target =
      def.type === "checkbox"
        ? option.querySelector(".add-filter-name")
        : option.querySelector('input:not([type="checkbox"])') ||
          option.querySelector(".add-filter-name");
    target?.focus();
  });
}

// Ao navegar para fora, opção expandida que ficou vazia volta a recolher.
function collapseFilterIfEmpty(key) {
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!def || def.type === "checkbox" || hasFilterValue(def)) return;
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

// Setas movem a seleção entre as opções: cima/baixo percorrem a coluna (com
// volta); esquerda/direita alternam entre as colunas mantendo a linha.
// Em campos já preenchidos, ←/→ continuam movendo o cursor do input.
function handleAddFilterMenuKeydown(event) {
  // Tab não circula entre as opções — a navegação é pelas setas.
  if (event.key === "Tab") {
    event.preventDefault();
    return;
  }
  // Enter fecha o menu; os valores preenchidos viram chips.
  if (event.key === "Enter") {
    event.preventDefault();
    closeAddFilterDropdown();
    return;
  }
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key))
    return;

  const optionEl = event.target.closest("[data-filter-key]");
  if (!optionEl) return;
  const currentKey = optionEl.dataset.filterKey;

  const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
  const isTextInput =
    event.target.matches("input") && event.target.type !== "checkbox";
  if (horizontal && isTextInput && event.target.value !== "") return;

  const columns = addFilterColumns.value;
  let col = columns.findIndex((c) => c.some((f) => f.key === currentKey));
  if (col === -1) return;
  let row = columns[col].findIndex((f) => f.key === currentKey);

  if (event.key === "ArrowDown") {
    row = (row + 1) % columns[col].length;
  } else if (event.key === "ArrowUp") {
    row = (row - 1 + columns[col].length) % columns[col].length;
  } else {
    const nextCol = (col + 1) % columns.length;
    if (!columns[nextCol]?.length) return;
    col = nextCol;
    row = Math.min(row, columns[col].length - 1);
  }

  event.preventDefault();
  collapseFilterIfEmpty(currentKey);
  selectFilterOption(columns[col][row].key);
}

function addFilter(key) {
  if (activeFilterKeys.value.includes(key)) return;
  activeFilterKeys.value.push(key);
  addedInThisSession.value.push(key);
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  // Checkbox adicionado já entra ativo — adicionar o filtro é a própria intenção.
  if (def?.type === "checkbox") local[key] = true;
  focusAddedFilterInput(key);
}

// Ao adicionar um filtro pelo menu "Adicionar filtro", foca automaticamente o
// respectivo campo (após o v-if renderizar o input) para o usuário já digitar.
// Checkbox não tem campo de texto, então é ignorado.
function focusAddedFilterInput(key) {
  nextTick(() => {
    const menu = addFilterMenuRef.value;
    if (!menu) return;
    const option = menu.querySelector(`[data-filter-key="${CSS.escape(key)}"]`);
    const input = option?.querySelector('input:not([type="checkbox"])');
    if (input) input.focus();
  });
}

// Clique na opção: abre o campo; se já estiver aberto e sem preenchimento, recolhe.
function toggleFilterOption(key) {
  if (!activeFilterKeys.value.includes(key)) {
    addFilter(key);
    return;
  }
  const def = ADVANCED_FILTERS.find((f) => f.key === key);
  if (!hasFilterValue(def)) removeFilter(key);
}

function removeFilter(key) {
  const def = ALL_FILTERS.find((f) => f.key === key);
  if (def?.type === "dateRange") {
    local[def.fromKey] = "";
    local[def.toKey] = "";
  } else if (def?.type === "checkbox") {
    local[key] = false;
  } else if (def) {
    local[key] = "";
  }
  activeFilterKeys.value = activeFilterKeys.value.filter((k) => k !== key);
  addedInThisSession.value = addedInThisSession.value.filter((k) => k !== key);
}

// Clique na chip → editar o filtro correspondente. Filtros avançados reabrem no
// dropdown "Adicionar filtro" já em modo de edição; filtros da linha principal
// (base) apenas focam o próprio campo/botão, sem duplicar dentro do dropdown.
function editFilter(key) {
  if (PRIMARY_KEYS.includes(key)) {
    closeAddFilterDropdown();
    focusBaseFilter(key);
    return;
  }
  openAddFilterDropdown(key);
}

function focusBaseFilter(key) {
  const refs = {
    activityTitle: activityTitleInputRef,
    textTitle: textTitleInputRef,
    onlyMine: myActivitiesButtonRef,
  };
  nextTick(() => {
    const el = refs[key]?.value;
    if (!el) return;
    el.focus();
    if (typeof el.select === "function") el.select();
  });
}

function formatChipDate(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const [year, month, day] = raw.split("-");
  return day && month && year ? `${day}/${month}/${year.slice(-2)}` : raw;
}

// Rótulo da chip — curto e semântico. Os campos de preenchimento vivem no dropdown.
function chipLabel(filter) {
  if (filter.type === "checkbox") return filter.short;

  if (filter.type === "dateRange") {
    const from = formatChipDate(local[filter.fromKey]);
    const to = formatChipDate(local[filter.toKey]);
    if (from && to) return `${filter.short}: ${from} → ${to}`;
    if (from) return `${filter.short} ≥ ${from}`;
    if (to) return `${filter.short} ≤ ${to}`;
    return `${filter.short}…`;
  }

  const raw = String(local[filter.key] ?? "").trim();
  if (!raw) return `${filter.short}…`;
  // Filtros com vocabulário próprio (progresso) exibem o rótulo, não o valor
  const value = filter.formatValue ? filter.formatValue(raw) : raw;
  // "quoted": o próprio rótulo já diz a relação (ex.: Compartilhada com grupo "Crianças")
  if (filter.verb === "quoted") return `${filter.short} "${value}"`;
  return filter.verb === "="
    ? `${filter.short} = ${value}`
    : `${filter.short} contém "${value}"`;
}

// ─────────────────────────────────────────────────────────────────────────────
// BOTÃO "MINHAS ATIVIDADES"
// ─────────────────────────────────────────────────────────────────────────────
const currentLoggedUserName = computed(() =>
  String(userStore.currentUser?.userName ?? "").trim()
);

const isMyActivitiesActive = computed(() => {
  return store.filters.onlyMine === true;
});

function toggleMyActivities() {
  const loggedUserName = currentLoggedUserName.value;

  if (!loggedUserName) {
    showErrorToast("Não foi possível identificar o usuário logado.");
    return;
  }

  // O watcher de onlyMine grava o filtro na store; o watcher de store.filters
  // volta à página 1 e refaz a busca.
  local.onlyMine = !isMyActivitiesActive.value;
}

// ─────────────────────────────────────────────────────────────────────────────
// SELEÇÃO / PERMISSÕES
// ─────────────────────────────────────────────────────────────────────────────
function hasPermission(act, ...names) {
  const perms = act?.userPermissions ?? [];
  return names.some((name) =>
    perms.some(
      (p) => (p.permissionName ?? "").toLowerCase() === name.toLowerCase()
    )
  );
}

// ── NOVO: guards combinam vínculo (portão) AND grupo (filtro interno) ─────────
function canEdit(act) {
  return memberCanEditActivity.value && hasPermission(act, "editar");
}
function canDelete(act) {
  return (
    memberCanDeleteActivity.value && hasPermission(act, "excluir", "deletar")
  );
}
function isActivityCreator(act) {
  return userStore.currentUser?.userId === act?.creatorId;
}

const anyEditableOnPage = computed(() =>
  store.filteredActivities.some((a) => canEdit(a))
);

const isAllSelectedOnPage = computed({
  get: () => {
    const editableIds = store.filteredActivities
      .filter((a) => canEdit(a))
      .map((a) => a.activityId);
    if (editableIds.length === 0) return false;
    return editableIds.every((id) => store.selectedActivityIds.includes(id));
  },
  set: (value) => {
    const editableIds = store.filteredActivities
      .filter((a) => canEdit(a))
      .map((a) => a.activityId);
    if (value) {
      editableIds.forEach((id) => {
        if (!store.selectedActivityIds.includes(id))
          store.selectedActivityIds.push(id);
      });
    } else {
      store.selectedActivityIds = store.selectedActivityIds.filter(
        (id) => !editableIds.includes(id)
      );
    }
  },
});

const deletableIdsOnPage = computed(
  () =>
    new Set(
      store.filteredActivities
        .filter((a) => canDelete(a))
        .map((a) => a.activityId)
    )
);

const canBatchActions = computed(
  () =>
    Array.isArray(store.selectedActivityIds) &&
    store.selectedActivityIds.some((id) => deletableIdsOnPage.value.has(id))
);

function toggleSelection(id) {
  store.toggleSelection(id);
}

// ─────────────────────────────────────────────────────────────────────────────
// AÇÕES DE LINHA
// ─────────────────────────────────────────────────────────────────────────────
function toggleRowDropdown(id, event) {
  if (openDropdownId.value === id) {
    openDropdownId.value = null;
    return;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  // Posição provisória (oculta) até a medição decidir o lado definitivo.
  dropdownPosition.value = { top: rect.top, left: rect.right + 8, ready: false };
  openDropdownId.value = id;
  openProgressDropdownId.value = null;
  positionFixedMenu(".row-dropdown-menu", rect, "right", dropdownPosition);
}

function handleView(act) {
  selectedActivity.value = act;
  selectedActivityId.value = act.activityId;
  store.selectedActivity = act;
  // ✅ FIX: ao abrir o card, zera o flag de alteração (abertura ≠ mudança)
  cardActivityChanged.value = false;
  showCardModal.value = true;
  openDropdownId.value = null;
}

function handleViewPermissions(act) {
  if (!act?.activityId) return;
  selectedActivity.value = act;
  selectedActivityId.value = act.activityId;
  store.selectedActivity = act;
  showPermissionViewModal.value = true;
  openDropdownId.value = null;
}
function closePermissionViewModal() {
  showPermissionViewModal.value = false;
  selectedActivityId.value = null;
}

function handleEdit(act) {
  selectedActivity.value = act;
  selectedActivityId.value = act.activityId;
  store.selectedActivity = act;
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleDelete(act) {
  selectedActivity.value = act;
  store.selectedActivity = act;
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

// O clique da linha leva a anotar, então carrega os guards que antes ficavam no
// botão: o portão do vínculo (memberCanViewNotes) e a permissão por atividade.
// Como uma linha não tem estado "desabilitado com tooltip", canOpenNotes também
// governa a classe .clickable-row — sem permissão, a linha nem exibe o cursor
// de mão, para o clique morto não parecer defeito.
function canOpenNotes(act) {
  return (
    memberCanViewNotes.value && hasPermission(act, "selecionar") && !!act?.active
  );
}

function handleRowClick(act) {
  if (!canOpenNotes(act)) return;
  goToRightArea(act);
}

// ✅ FIX: chamado pelo evento "activity-updated" do ActivityCardModal.
// O card só emite isso após edição (handleEditRefresh) ou exclusão (handleDeleteClose).
function handleCardActivityUpdated() {
  cardActivityChanged.value = true;
}

// ✅ FIX: fechamento do card. Recarrega a tabela SOMENTE se houve edição/exclusão.
// Visualização pura (abrir e fechar sem alterar nada) não dispara fetchActivities().
function handleCardModalClose() {
  showCardModal.value = false;

  if (cardActivityChanged.value) {
    fetchActivities();
  }

  // zera o flag para o próximo ciclo de abertura do card
  cardActivityChanged.value = false;
}

// ─────────────────────────────────────────────────────────────────────────────
// BATCH DELETE
// ─────────────────────────────────────────────────────────────────────────────
function tryOpenBatchDeleteModal() {
  const eligible = store.selectedActivityIds.filter((id) =>
    deletableIdsOnPage.value.has(id)
  );
  if (eligible.length === 0) {
    showErrorToast("Nenhuma atividade selecionada com permissão de exclusão.");
    isBatchDropdownOpen.value = false;
    return;
  }
  selectedBatchIds.value = eligible;
  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH
// ─────────────────────────────────────────────────────────────────────────────
async function fetchActivities() {
  const requestId = ++fetchActivitiesRequestId.value;

  try {
    // ✅ PATCH: enquanto busca, não exibe o empty-state de "nenhuma atividade"
    isLoadingActivities.value = true;

    await store.fetchActivitiesGeneralPaged(currentPage.value, pageSize.value);
  } catch (error) {
    console.error("❌ Erro ao buscar atividades:", error?.message);
    showErrorToast("Erro ao carregar atividades.");
  } finally {
    // ✅ PATCH: só a requisição mais recente pode encerrar o loading
    if (requestId === fetchActivitiesRequestId.value) {
      isLoadingActivities.value = false;
    }
  }
}
watch(currentPage, fetchActivities);

// ─────────────────────────────────────────────────────────────────────────────
// HANDLER PÓS-MUDANÇA
// ─────────────────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
async function handleActivitiesChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;
  if (clearSelection) store.clearSelection();
  if (successMessage) showSuccessToast(successMessage);
  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchActivities();
    return;
  }
  await fetchActivities();
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMATAÇÃO & HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function getStatusClass(statusName) {
  const name = (statusName ?? "").toLowerCase();
  if (name.includes("em correção")) return "status-red";
  if (name.includes("concluída para correção")) return "status-orange";
  if (name.includes("concluída")) return "status-blue";
  if (name.includes("corrigida")) return "status-green";
  if (name.includes("em andamento")) return "status-yellow";
  return "";
}

// ── Progresso: dropdown inline por linha (persiste + auto-data em "Concluída") ─
function toggleProgressDropdown(act, event) {
  event.stopPropagation();
  if (isProgressUpdating.value) return;
  if (openProgressDropdownId.value === act.activityId) {
    openProgressDropdownId.value = null;
    return;
  }
  const rect = event.currentTarget.getBoundingClientRect();
  // Posição provisória (oculta) até a medição decidir se abre para baixo ou
  // para cima — nas últimas linhas não há espaço abaixo.
  progressDropdownPosition.value = {
    top: rect.bottom + 4,
    left: rect.left,
    ready: false,
  };
  openProgressDropdownId.value = act.activityId;
  openDropdownId.value = null;
  positionFixedMenu(
    ".progress-dropdown-menu",
    rect,
    "below",
    progressDropdownPosition
  );
}

async function handleProgressChange(act, newStatus) {
  if (isSameProgress(act.progress, newStatus)) {
    openProgressDropdownId.value = null;
    return;
  }

  isProgressUpdating.value = true;
  openProgressDropdownId.value = null;
  try {
    // Busca o estado completo para não sobrescrever descrição/permissões no PUT.
    const full = await activityStore.getActivityById(act.activityId);
    const rawPerms = await activityStore.fetchActivityPermissions(
      act.activityId
    );
    const permissions = (rawPerms || []).map((p) => ({
      entityId: p.userId != null ? p.userId : p.groupId,
      permissionId: p.permissionId,
      type: p.userId != null ? "user" : "group",
    }));

    // "Concluída" exige Data de Fim: se ainda não houver, atribui a data/hora
    // atual no mesmo formato datetime-local usado no EditActivityForm.
    let endedAt = full.endedAt ?? null;
    if (newStatus === "Concluída" && !endedAt) {
      endedAt = formatDateTimeForInput(new Date());
    }

    await activityStore.updateActivity({
      activityId: full.activityId,
      title: full.title,
      description: full.description,
      status: newStatus,
      active: full.active,
      startedAt: full.startedAt ?? null,
      endedAt,
      textId: full.textId,
      permissions,
    });

    await fetchActivities();
    showSuccessToast(
      `Progresso da atividade "${toTitleCase(
        act.activityTitle
      )}" alterado para "${progressFullLabel(newStatus)}" com sucesso!`
    );
  } catch (error) {
    console.error("❌ Falha ao atualizar o progresso da atividade:", error);
    showErrorToast(
      error?.response?.data?.message ||
        error?.message ||
        "Ocorreu um erro ao atualizar o progresso."
    );
  } finally {
    isProgressUpdating.value = false;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CLICK FORA
// ─────────────────────────────────────────────────────────────────────────────
function handleClickOutside(event) {
  if (batchDropdownRef.value && !batchDropdownRef.value.contains(event.target))
    isBatchDropdownOpen.value = false;
  if (openDropdownId.value && !event.target.closest(".action-cell-container") && !event.target.closest(".row-dropdown-menu"))
    openDropdownId.value = null;

  if (
    openProgressDropdownId.value &&
    !event.target.closest(".progress-cell-container") &&
    !event.target.closest(".progress-dropdown-menu")
  )
    openProgressDropdownId.value = null;

  if (
    isAddFilterOpen.value &&
    !event.target.closest(".add-filter-dropdown-anchor")
  )
    closeAddFilterDropdown();

  // Foco saiu do botão "Mais filtros" e do painel sem nenhum filtro
  // efetivamente marcado → o botão volta ao estado desmarcado.
  if (
    isAdvancedPanelOpen.value &&
    !event.target.closest(".advanced-filters-anchor") &&
    !event.target.closest(".active-filters-panel") &&
    panelOnlyChips.value.length === 0
  ) {
    isAdvancedPanelOpen.value = false;
    closeAddFilterDropdown();
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  // capture: scroll não borbulha — é assim que a rolagem da .scroll-area chega aqui
  window.addEventListener("scroll", handleViewportChange, true);
  window.addEventListener("resize", handleViewportChange);
  // Busca inicial: os filtros já podem vir preenchidos de outra tela (o card
  // "Minhas atividades" do Dashboard grava onlyMine antes de navegar).
  fetchActivities();
  // A tela abre pronta para filtrar, com o cursor no primeiro campo.
  focusBaseFilter("activityTitle");
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", handleViewportChange, true);
  window.removeEventListener("resize", handleViewportChange);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  Object.keys(local).forEach((k) => {
    local[k] = typeof local[k] === "boolean" ? false : "";
  });
  activeFilterKeys.value = [];
  addedInThisSession.value = [];
  store.clearFilters();
  store.clearSelection();
  openDropdownId.value = null;
  openProgressDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedBatchIds.value = [];
});
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <!-- ── CABEÇALHO ──────────────────────────────────────────────────────── -->
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Pesquisa por Atividades</div>
          <div class="text-sm mt-1 text-gray-700 ">
            <template v-if="isSys"
              >Visualize e gerencie as atividades de todos os usuários do
              sistema.</template
            >
            <template v-else
              >Acompanhe suas atividades, progresso e notas
              vinculadas.</template
            >
          </div>
        </div>
      </div>

      <!-- Área rolável: cabeçalho fica fixo; filtros, tabela e paginador rolam -->
      <div class="scroll-area">
      <!-- ── BARRA DE FILTROS (inclui botões de ação na mesma linha) ──────── -->
      <div class="action-bar">
        <div class="filter-panel w-full">
          <div class="filter-row">
            <!-- Título da atividade -->
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Título da atividade</label>
              <input
                ref="activityTitleInputRef"
                v-model="local.activityTitle"
                type="text"
                placeholder="ex: Capítulo 1…"
                class="filter-input"
              />
            </div>

            <!-- Título do texto -->
            <div class="filter-field filter-field-wide">
              <label class="filter-label">Título do texto</label>
              <input
                ref="textTitleInputRef"
                v-model="local.textTitle"
                type="text"
                placeholder="ex: Dom Quixote…"
                class="filter-input"
              />
            </div>

            <div class="filter-actions-right">
              <div
                class="filter-field filter-field-batch-actions advanced-filters-anchor"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  type="button"
                  class="btn btn-batch btn-advanced"
                  :class="{ active: isPanelVisible }"
                  title="Exibir mais filtros"
                  @click.stop="toggleAdvancedPanel"
                >
                  <i class="fas fa-filter"></i>
                  <span>Mais filtros</span>
                  <span v-if="chipFilters.length" class="advanced-count">{{
                    chipFilters.length
                  }}</span>
                  <svg
                    class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                    :class="{ open: isAdvancedPanelOpen }"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      clip-rule="evenodd"
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    />
                  </svg>
                </button>
              </div>

              <div
                v-if="hasActiveFilters"
                class="filter-field filter-field-clear"
              >
                <label class="filter-label">&nbsp;</label>
                <button
                  class="filter-clear"
                  title="Limpar filtros"
                  @click="clearAllFilters"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="clear-icon"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>Limpar</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Painel de filtros ativos -->
          <div v-if="isPanelVisible" class="active-filters-panel">
            <div class="active-filters-title">
              <i class="fas fa-filter"></i>
              <span>Filtros ativos</span>
            </div>

            <div v-if="chipFilters.length" class="active-filters-chips">
              <div
                v-for="filter in chipFilters"
                :key="filter.key"
                class="filter-chip filter-chip--clickable"
                :title="chipLabel(filter)"
                role="button"
                tabindex="0"
                @click.stop="editFilter(filter.key)"
                @keydown.enter.prevent="editFilter(filter.key)"
                @keydown.space.prevent="editFilter(filter.key)"
              >
                <i :class="filter.icon" class="filter-chip-icon"></i>
                <span class="filter-chip-label">{{ chipLabel(filter) }}</span>
                <button
                  type="button"
                  class="filter-chip-remove"
                  :title="`Remover filtro ${filter.short}`"
                  @click.stop="removeFilter(filter.key)"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <p v-else class="active-filters-empty">Nenhum filtro adicionado.</p>

            <div
              class="add-filter-dropdown-anchor"
              style="position: relative; align-self: flex-start"
            >
              <button
                type="button"
                class="btn btn-batch btn-add-filter"
                :disabled="!hasAddFilterContent"
                @click.stop="toggleAddFilterDropdown"
              >
                <i class="fas fa-plus"></i>
                <span>Adicionar filtro</span>
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isAddFilterOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div
                v-if="isAddFilterOpen && addFilterOptions.length > 0"
                ref="addFilterMenuRef"
                class="dropdown-menu add-filter-menu"
                @keydown="handleAddFilterMenuKeydown"
              >
                <div class="add-filter-grid">
                  <div
                    v-for="(column, columnIndex) in addFilterColumns"
                    :key="`add-filter-col-${columnIndex}`"
                    class="add-filter-column"
                  >
                    <div
                      v-for="filter in column"
                      :key="filter.key"
                      :data-filter-key="filter.key"
                      class="add-filter-option"
                      :class="{
 'is-open': isFilterAdded(filter.key),
                        'is-checkbox': filter.type === 'checkbox',
                      }"
                      @click.stop="toggleFilterOption(filter.key)"
                    >
                      <button
                        type="button"
                        class="add-filter-name"
                        @click.stop="toggleFilterOption(filter.key)"
                      >
                        <i :class="filter.icon" class="add-filter-icon"></i>
                        <span>{{ filter.label }}</span>
                      </button>

                      <template v-if="isFilterAdded(filter.key)">
                        <template v-if="filter.type === 'dateRange'">
                          <div class="add-filter-inputs">
                            <span class="add-filter-sublabel">de</span>
                            <input
                              v-model="local[filter.fromKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                            <span class="add-filter-sublabel">até</span>
                            <input
                              v-model="local[filter.toKey]"
                              type="date"
                              class="add-filter-input add-filter-input-date"
                              @click.stop
                            />
                          </div>
                        </template>

                        <template v-else-if="filter.type === 'select'">
                          <select
                            v-model="local[filter.key]"
                            class="add-filter-input add-filter-select"
                            @click.stop
                          >
                            <option value="">Todos</option>
                            <option
                              v-for="option in filter.options"
                              :key="option.value"
                              :value="option.value"
                            >
                              {{ option.label }}
                            </option>
                          </select>
                        </template>

                        <template v-else>
                          <input
                            v-model="local[filter.key]"
                            type="text"
                            :inputmode="
                              filter.type === 'numeric' ? 'numeric' : undefined
                            "
                            :placeholder="filter.placeholder"
                            class="add-filter-input"
                            @click.stop
                          />
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="filter-secondary-row">
            <button
              ref="myActivitiesButtonRef"
              type="button"
              class="btn btn-batch btn-my-activities"
              :class="{ active: isMyActivitiesActive }"
              title="Filtrar apenas minhas atividades"
              @click="toggleMyActivities"
            >
              <i class="fas fa-user"></i>
              <span>Minhas atividades</span>
            </button>

            <div ref="batchDropdownRef" class="relative">
              <button
                @click="
                  canBatchActions && (isBatchDropdownOpen = !isBatchDropdownOpen)
                "
                class="btn btn-batch"
                :disabled="!canBatchActions"
              >
                Ações em lote
                <svg
                  class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                  :class="{ open: isBatchDropdownOpen }"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  />
                </svg>
              </button>

              <div v-if="isBatchDropdownOpen" class="dropdown-menu">
                <div class="py-1">
                  <a
                    href="#"
                    role="button"
                    @click.prevent="
                      memberCanDeleteActivity && tryOpenBatchDeleteModal()
                    "
                    class="dropdown-item danger-item"
                    :class="{
 'dropdown-item-disabled': !memberCanDeleteActivity,
                    }"
                    :title="
                      !memberCanDeleteActivity
                        ? 'Sem permissão para excluir atividades neste vínculo'
                        : undefined
                    "
                  >
                    <i class="fas fa-trash-alt"></i>
                    <span>Apagar selecionadas</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ✅ REFACTOR: tabela + paginador juntos num wrapper que cresce -->
      <div class="content-wrapper">
        <div class="table-wrapper">
          <table class="main-table">
            <thead>
              <tr>
                <th style="width: 4%">
                  <input
                    id="checkbox-all"
                    type="checkbox"
                    v-model="isAllSelectedOnPage"
                    class="checkbox-style"
                    :disabled="!anyEditableOnPage"
                    :class="{ 'disabled-item': !anyEditableOnPage }"
                  />
                </th>
                <th style="width: 24px">Ações</th>
                <th style="width: 5%">ID Ativ.</th>
                <th style="width: 23%">Nome Atividade</th>
                <th style="width: 16%">Título Texto</th>
                <!-- Recebeu os 5% da antiga coluna "ID Texto": com 14% o rótulo
                     do progresso cabe em uma linha até em telas estreitas -->
                <th style="width: 14%">Progresso</th>
                <th style="width: 5%">Notas</th>
                <th style="width: 8%">Data Início</th>
                <th style="width: 8%">Data Fim</th>
                <th style="width: 8%">Criador</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="act in !isLoadingActivities
                  ? store.filteredActivities
                  : []"
                :key="act.activityId"
                :class="{ 'clickable-row': canOpenNotes(act) }"
                :title="
                  canOpenNotes(act)
                    ? null
                    : 'Sem permissão para anotar nesta atividade'
                "
                @click="handleRowClick(act)"
              >
                <td @click.stop>
                  <input
                    type="checkbox"
                    :value="act.activityId"
                    :checked="
                      store.selectedActivityIds?.includes(act.activityId)
                    "
                    @change="toggleSelection(act.activityId)"
                    class="checkbox-style"
                    :disabled="!canEdit(act)"
                    :class="{ 'disabled-item': !canEdit(act) }"
                  />
                </td>

                <td @click.stop class="action-cell-container">
                  <div class="row-actions">
                    <button
                      @click="handleView(act)"
                      class="btn-icon btn-slate"
                      title="Detalhes da atividade"
                    >
                      <i class="fas fa-info-circle fa-lg"></i>
                    </button>
                    <button
                      @click="toggleRowDropdown(act.activityId, $event)"
                      class="btn-icon btn-soft-brand"
                      title="Menu de Ações"
                    >
                      <i class="fas fa-ellipsis-h fa-lg"></i>
                    </button>
                  </div>

                  <Teleport to="body">
                    <div
                      v-if="openDropdownId === act.activityId"
                      class="row-dropdown-menu"
                      :style="{
                        top: dropdownPosition.top + 'px',
                        left: dropdownPosition.left + 'px',
                        visibility: dropdownPosition.ready ? 'visible' : 'hidden',
                      }"
                    >
                      <ul>
                        <li @click="handleView(act)">
                          <i class="fas fa-info-circle"></i
                          ><span>Detalhes da atividade</span>
                        </li>
                        <li
                          v-if="isActivityCreator(act)"
                          @click="handleViewPermissions(act)"
                        >
                          <i class="fas fa-user-shield"></i
                          ><span>Ver permissões</span>
                        </li>

                        <!-- Editar -->
                        <li
                          @click="canEdit(act) && handleEdit(act)"
                          :class="{ 'dropdown-item-disabled': !canEdit(act) }"
                          :title="
                            !canEdit(act)
                              ? 'Sem permissão para editar atividades neste vínculo'
                              : undefined
                          "
                        >
                          <i class="fas fa-pencil-alt"></i><span>Editar</span>
                        </li>

                        <!-- Apagar -->
                        <li
                          class="danger-item"
                          @click="canDelete(act) && handleDelete(act)"
                          :class="{ 'dropdown-item-disabled': !canDelete(act) }"
                          :title="
                            !canDelete(act)
                              ? 'Sem permissão para excluir atividades neste vínculo'
                              : undefined
                          "
                        >
                          <i class="fas fa-trash-alt"></i><span>Apagar</span>
                        </li>
                      </ul>
                    </div>
                  </Teleport>
                </td>

                <td class="cell-text-center">
                  <span :title="act.activityId" style="font-size: smaller">{{
                    act.activityId
                  }}</span>
                </td>
                <td class="cell-text-left">
                  <span :title="act.activityTitle">{{
                    act.activityTitle ?? "—"
                  }}</span>
                </td>
                <td class="cell-text-left">
                  <span :title="act.textTitle">{{ act.textTitle ?? "—" }}</span>
                </td>
                <td @click.stop class="progress-cell-container">
                  <button
                    class="progress-badge"
                    :class="getStatusClass(act.progress)"
                    @click="canEdit(act) && toggleProgressDropdown(act, $event)"
                    :disabled="isProgressUpdating || !canEdit(act)"
                    :title="
                      canEdit(act)
                        ? `${progressFullLabel(
                            act.progress
                          )} — clique para alterar o progresso`
                        : `${progressFullLabel(
                            act.progress
                          )} — sem permissão para editar esta atividade`
                    "
                  >
                    <span class="progress-badge-text">{{
                      progressShortLabel(act.progress)
                    }}</span>
                    <i
                      v-if="canEdit(act)"
                      class="fas fa-chevron-down progress-chevron"
                    ></i>
                  </button>

                  <Teleport to="body">
                    <div
                      v-if="openProgressDropdownId === act.activityId"
                      class="progress-dropdown-menu"
                      :style="{
                        top: progressDropdownPosition.top + 'px',
                        left: progressDropdownPosition.left + 'px',
                        visibility: progressDropdownPosition.ready
                          ? 'visible'
                          : 'hidden',
                      }"
                    >
                      <ul>
                        <li
                          v-for="opt in PROGRESS_OPTIONS"
                          :key="opt.value"
                          class="progress-option"
                          :class="[
 getStatusClass(opt.value),
                            isSameProgress(act.progress, opt.value)
                              ? 'progress-option--current'
                              : '',
                          ]"
                          @click="handleProgressChange(act, opt.value)"
                          :title="opt.label"
                        >
                          {{ opt.short }}
                          <i
                            v-if="isSameProgress(act.progress, opt.value)"
                            class="fas fa-check progress-check-icon"
                          ></i>
                        </li>
                      </ul>
                    </div>
                  </Teleport>
                </td>
                <td class="cell-text-center">
                  <span
                    class="chips-note-count"
                    :class="{
 'active-notes':
                        (act.noteCount ?? 0) + (act.commentCount ?? 0) > 0,
                    }"
                    :title="`${
                      (act.noteCount ?? 0) + (act.commentCount ?? 0)
                    } notas e comentários`"
                  >
                    {{ (act.noteCount ?? 0) + (act.commentCount ?? 0) }}
                  </span>
                </td>
                <td class="cell-text-center">
                  <span
                    :title="formatDate(act.startedAt)"
                    style="font-size: smaller"
                    >{{ formatDate(act.startedAt) }}</span
                  >
                </td>
                <td class="cell-text-center">
                  <span
                    :title="act.endedAt ? formatDate(act.endedAt) : '—'"
                    style="font-size: smaller"
                    >{{ act.endedAt ? formatDate(act.endedAt) : "—" }}</span
                  >
                </td>
                <td>
                  <span :title="act.creatorName" style="font-size: smaller">{{
                    act.creatorName ?? "—"
                  }}</span>
                </td>
              </tr>

              <!-- ✅ PATCH: estado de carregamento -->
              <tr v-if="isLoadingActivities">
                <td colspan="10" class="empty-row empty-row-state">
                  <div class="empty-state-content">
                    <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      />
                    </svg>

                    <p>Carregando atividades...</p>
                  </div>
                </td>
              </tr>

              <!-- ✅ PATCH: estado vazio real -->
              <tr v-else-if="store.filteredActivities.length === 0">
                <td colspan="10" class="empty-row empty-row-state">
                  <div class="empty-state-content">
                    <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                    <p>Nenhuma atividade encontrada.</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- fim table-wrapper -->

        <!-- paginador colado logo após a tabela, dentro do content-wrapper -->
        <div
          v-if="!isLoadingActivities && (store.totalCount ?? 0) > 0"
          class="sub-pagination-row"
        >
          <div class="sub-pagination-left">
            <span class="sub-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="store.totalCount ?? 0"
            />
            <span>registros</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="store.totalCount ?? 0"
            :total-pages="store.totalPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
        <!-- fim sub-pagination-row -->
      </div>
      <!-- fim content-wrapper -->
      </div>
      <!-- fim scroll-area -->
    </div>
    <!-- fim main-container -->
  </div>
  <!-- fim w-full h-full -->

  <Teleport to="body">
    <!-- ✅ FIX: escuta "activity-updated" para saber se houve edição/exclusão.
         O @close agora recarrega a tabela SOMENTE quando houve alteração. -->
    <ActivityCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :activityId="selectedActivityId"
      @activity-updated="handleCardActivityUpdated"
      @close="handleCardModalClose"
      @notify="handleCardNotify"
    />
    <PermissionView
      v-if="showPermissionViewModal"
      :show="showPermissionViewModal"
      entityType="activity"
      :activityId="selectedActivityId"
      title="Permissões da atividade"
      @close="closePermissionViewModal"
    />

    <!-- ── NOVO: EditActivityGeneralForm condicionado ao guard de edit ─────── -->
    <EditActivityGeneralForm
      v-if="showEditModal && memberCanEditActivity"
      :activityId="selectedActivityId"
      @close="showEditModal = false"
      @refresh="
        handleActivitiesChanged({ goToFirstPage: false, closeEdit: true })
      "
    />

    <!-- ── NOVO: DeleteActivityGeneralForm condicionado ao guard de delete ─── -->
    <DeleteActivityGeneralForm
      v-if="showDeleteModal && memberCanDeleteActivity"
      :activity="selectedActivity"
      @close="showDeleteModal = false"
      @refresh="
        handleActivitiesChanged({
          goToFirstPage: true,
          closeDelete: true,
          clearSelection: true,
        })
      "
      @notify="handleChildNotify"
    />

    <!-- ── NOVO: BatchDeleteActivityGeneralForm condicionado ao guard de delete -->
    <BatchDeleteActivityGeneralForm
      v-if="showBatchDeleteModal && memberCanDeleteActivity"
      :activityIds="selectedBatchIds"
      @close="showBatchDeleteModal = false"
      @refresh="
        handleActivitiesChanged({
          goToFirstPage: true,
          closeBatchDelete: true,
          clearSelection: true,
        })
      "
      @notify="handleChildNotify"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* Rola tudo abaixo do cabeçalho (que fica fixo): filtros, tabela e paginador */
.scroll-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.main-header {
  flex-shrink: 0;
}

.action-bar {
  flex-shrink: 0;
}

.filter-panel {
  width: 100%;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 160px;
}
/* Os campos dividem igualmente toda a largura livre até o botão de filtros */
.filter-field-wide {
  flex: 1 1 0;
  min-width: 130px;
  max-width: none;
}
/* Botão Limpar — tamanho natural, alinhado à base dos campos */
.filter-field-clear {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: unset;
  max-width: unset;
}

/* Botões à direita da linha de filtros */
.filter-actions-right {
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 0 0 auto;
  min-width: max-content;
}
.filter-field-batch-actions {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}

/* Minhas atividades / Ações em lote — abaixo do painel, à direita */
.filter-secondary-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.filter-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-light);
  white-space: nowrap;
  padding-left: 2px;
}

.filter-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  border-radius: 6px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}
.filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
.filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: auto;
  height: 32px;
  padding: 0 0.65rem;
  flex-shrink: 0;
  border: 1px solid var(--danger);
  border-radius: 6px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.15s;
  white-space: nowrap;
}
/* mesmo comportamento de clique do botão "Mais filtros" (.btn-batch) */
.filter-clear:hover {
  background-color: var(--danger-bg);
}
.filter-clear:active {
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  transform: translateY(1px);
}

/* ── Painel "Filtros ativos" + chips + menu "Adicionar filtro" ───────────────
   Espelha NoteGeneralForm.vue; paleta do card de metadados de TextDisplayer. */
.btn-advanced.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}
.btn-advanced.active:hover {
  background-color: var(--brand-light-hover);
}
.advanced-count {
  display: inline-grid;
  place-items: center;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  padding: 0;
  border-radius: 999px;
  background-color: var(--text-on-brand-soft);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  flex-shrink: 0;
}
.active-filters-panel {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.75rem;
  padding: 14px 18px 16px;
  border: 1px solid rgba(202, 138, 4, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.32);
  box-shadow: 0 2px 8px rgba(120, 53, 15, 0.06);
}
.active-filters-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-dark);
}
.active-filters-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.active-filters-empty {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-light);
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 32px;
  padding: 0 0.35rem 0 0.6rem;
  border: 1px solid #ded5ff;
  /* mesmo raio do botão "Mais filtros" (.btn-batch) */
  border-radius: 10px;
  background-color: #ece7ff;
  color: #4b3f86;
}
.filter-chip--clickable {
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}
.filter-chip--clickable:hover {
  background-color: #ddd3ff;
  border-color: #c9bcff;
}
.filter-chip--clickable:focus-visible {
  outline: 2px solid #7c5cff;
  outline-offset: 1px;
}
.filter-chip-icon {
  width: 13px;
  text-align: center;
  font-size: 0.72rem;
  color: inherit;
  opacity: 0.75;
  flex-shrink: 0;
}
.filter-chip-label {
  font-size: 0.76rem;
  font-weight: 600;
  color: inherit;
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-chip-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: inherit;
  opacity: 0.65;
  font-size: 0.7rem;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}
.filter-chip-remove:hover {
  background-color: var(--danger-bg);
  color: var(--danger-text);
  opacity: 1;
}
.btn-add-filter {
  gap: 0.4rem;
}
.add-filter-menu {
  right: auto;
  left: 0;
  /* alto o bastante para caber a coluna inteira com campos abertos, sem rolagem */
  max-height: min(560px, calc(100vh - 160px));
  overflow-y: auto;
  padding: 0.4rem;
}
/* duas colunas independentes: abrir um campo cresce só a coluna dele */
.add-filter-grid {
  display: flex;
  align-items: stretch;
}
.add-filter-column {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 330px;
  flex: 0 0 330px;
}
.add-filter-column + .add-filter-column {
  border-left: 1px solid var(--border-color);
}
/* o campo abre sempre na linha de baixo, ocupando a largura da coluna */
.add-filter-option {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.3rem;
  padding: 0.25rem 0.7rem;
  border-radius: 8px;
  min-width: 0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.add-filter-option:hover {
  background-color: #f5f3ff;
}
/* foco (navegação por setas) espelha o hover na linha inteira */
.add-filter-option:focus-within {
  background-color: #f5f3ff;
}
.add-filter-name:focus {
  outline: none;
}
.add-filter-option.is-checkbox {
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}
.add-filter-name {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.4rem;
  border: none;
  background: transparent;
  padding: 0.2rem 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-dark);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}
.add-filter-icon {
  width: 14px;
  text-align: center;
  font-size: 0.78rem;
  color: var(--text-subtle);
  flex-shrink: 0;
}
.add-filter-inputs {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
  min-width: 0;
}
.add-filter-sublabel {
  font-size: 0.7rem;
  color: var(--text-light);
}
/* todos os campos com a mesma largura: a da coluna */
.add-filter-input {
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  height: 26px;
  padding: 0 0.4rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.78rem;
  box-sizing: border-box;
}
.add-filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}
/* o período divide a mesma largura entre as duas datas */
.add-filter-input-date {
  flex: 1 1 0;
  width: auto;
}
.add-filter-select {
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.35rem center;
  background-size: 16px 16px;
  padding-right: 1.5rem;
}
.clear-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}

/* ─────────────────────────────────────────────────────────────────────────────
   CONTENT WRAPPER
   Não cresce — altura natural (auto). O paginador fica imediatamente
   abaixo da última linha da tabela.
   ───────────────────────────────────────────────────────────────────────────── */
.content-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.cell-text-center {
  text-align: center;
}

.empty-state-content {
  width: 100%;
}

/* ─────────────────────────────────────────────────────────────────────────────
   STATUS TAGS
   ───────────────────────────────────────────────────────────────────────────── */
.status-tag {
  display: inline-block;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
}
.status-red {
  background-color: #ffc9c9;
  color: #c92a2a;
}
.status-orange {
  background-color: #ffd8a8;
  color: #e8590c;
}
.status-blue {
  background-color: #a5d8ff;
  color: #1971c2;
}
.status-green {
  background-color: #b2f2bb;
  color: #2b8a3e;
}
.status-yellow {
  background-color: #fff3bf;
  color: #f59f00;
}

/* ─────────────────────────────────────────────────────────────────────────────
   PROGRESSO — badge + dropdown inline (espelho do ActivityForm)
   ───────────────────────────────────────────────────────────────────────────── */
.progress-cell-container {
  position: relative;
}
.progress-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: filter 0.15s, transform 0.05s ease;
  /* Uma linha sempre: o rótulo curto cabe na coluna, e o ellipsis é a garantia
     final em telas muito estreitas (o nome completo fica no title) */
  max-width: 100%;
  white-space: nowrap;
}
.progress-badge-text {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.progress-chevron {
  flex-shrink: 0;
}
.progress-badge:hover:not(:disabled) {
  filter: brightness(0.93);
}
.progress-badge:active:not(:disabled) {
  transform: translateY(1px);
}
.progress-badge:focus,
.progress-badge:focus-visible {
  outline: none;
  box-shadow: none;
}
.progress-badge:disabled {
  cursor: not-allowed;
}
.progress-badge:disabled:hover {
  filter: none;
  transform: none;
}
.progress-chevron {
  font-size: 0.6rem;
  opacity: 0.7;
}

/* Menu teleportado para o body → position: fixed (como .row-dropdown-menu) */
.progress-dropdown-menu {
  position: fixed;
  z-index: 9999;
  min-width: 175px;
  background: var(--surface);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  box-shadow: var(--shadow-dropdown);
  /* Janela baixa demais para o menu inteiro: rola por dentro em vez de vazar */
  max-height: calc(100vh - 16px);
  overflow-y: auto;
}
.progress-dropdown-menu ul {
  list-style: none;
  padding: 0.3rem;
  margin: 0;
}
.progress-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.7rem;
  border-radius: 7px;
  font-size: 0.82rem;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}
/* Neutro por padrão; a cor (status-*) aparece no hover e permanece na atual */
.progress-option:not(:hover):not(.progress-option--current) {
  background-color: transparent;
  color: var(--text-dark);
}
.progress-option:active {
  transform: translateY(1px);
}
.progress-option--current {
  font-weight: 700;
}
.progress-check-icon {
  color: var(--brand-light);
  font-size: 0.75rem;
}

/* ─────────────────────────────────────────────────────────────────────────────
   CHIPS NOTAS
   ───────────────────────────────────────────────────────────────────────────── */
.chips-note-count {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 28px;
  height: 22px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-dark);
  background-color: var(--neutral-200);
  border: 1px solid #ced4da;
  transition: all 0.2s;
}
.chips-note-count.active-notes {
  background-color: #ccf7ff;
  color: #1a4a4f;
  border-color: #9bf6ff;
}

.btn-my-activities {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 0.8rem;
  font-size: 0.85rem;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background-color: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  white-space: nowrap;
  gap: 0.35rem;
}
.btn-my-activities:hover {
  background-color: var(--neutral-300);
}
.btn-my-activities:active {
  transform: translateY(1px);
}
.btn-my-activities.active {
  background-color: var(--brand-light);
  border-color: var(--brand-light);
  color: var(--text-on-brand);
}
.btn-my-activities.active:hover {
  background-color: var(--brand-light-hover);
}
.btn-my-activities.active:active {
  transform: translateY(1px);
}
.btn-my-activities i {
  font-size: 0.85rem;
}

.btn-icon-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
}
.btn-icon-disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}
.btn-pastel-blue {
  background-color: var(--focus-border);
  color: #1c3d5a;
}
.btn-pastel-blue:hover {
  background-color: #82b4ff;
}

/* Ação de consulta (card de detalhes): neutra, para não competir com a ação
   primária da linha. O ícone fica em tom médio de propósito — o disco sólido do
   info-circle pesa muito mais que os três pontos do menu de ações, e um slate
   escuro fazia o botão de consulta gritar mais alto que o de comandos. */
.btn-slate {
  background-color: var(--neutral-200);
  color: var(--text-muted);
}
.btn-slate:hover {
  background-color: var(--neutral-400);
}

/* ─────────────────────────────────────────────────────────────────────────────
   ROW ACTIONS & DROPDOWNS
   ───────────────────────────────────────────────────────────────────────────── */

.dropdown-item-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
  pointer-events: auto !important;
}
.dropdown-item-disabled:hover {
  background-color: transparent !important;
  transform: none;
  box-shadow: none;
}

.row-dropdown-menu {
  max-height: calc(100vh - 16px);
}

.row-dropdown-menu li.dropdown-item-disabled:hover {
  transform: none;
  box-shadow: none;
}

/* ─────────────────────────────────────────────────────────────────────────────
   ✅ REFACTOR: PAGINADOR — flex-shrink:0 garante que nunca seja cortado
   ───────────────────────────────────────────────────────────────────────────── */
.sub-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* ✅ REFACTOR: padding reduzido de 0.75rem/1rem para 0.4rem/0.5rem */
  padding: 0.4rem 1.5rem 0.5rem;
  font-size: 0.82rem;
  flex-shrink: 0;
}
.sub-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.sub-pagination-label {
  padding-top: 2px;
}

</style>